import { FormInputValues, Form, FormControlChangeListener, controlTag, FormControl } from '@/form';
import http from '@/http';
import { Controller } from '@hotwired/stimulus';


export interface ViewSwitcherVariables {
    viewId: string;
    inputs: FormInputValues;
    [name: string]: any;
}

type ViewRequestMode = 'push' | 'pop' | 'refresh';
/**
 * Note: You should never need to create more than one instance of the ViewSwitcher.
 * The ViewSwitcher takes care of initializing the view and updating the browser history.
 */

export class ViewSwitcher {

    public static initialize() {
        let switcher = new this(new Form, [], selectPageParams());
        window.addEventListener('popstate', switcher.onStateChange);
        return switcher;
    }

    /** 
     * The view id that was changed most recently, 
     * needed to restore state when the user navigates back or forward.
     * See explanation in {@link onStateChange} */
    private previousViewIds: string[] = [];

    private constructor(
        private form: Form,
        private anchorEls: HTMLAnchorElement[],
        private queryParams: QueryParams,
    ) { }

    public addSwitch(switchEl: HTMLElement) {
        // Initialize anchor switch
        if (switchEl instanceof HTMLAnchorElement) {
            switchEl.addEventListener('click', this.onAnchorClick);
            this.anchorEls.push(switchEl);
        }


        // Initialize control switch
        else {
            controlTag.get(switchEl).then(control => {
                control.addChangeListener(this.onControlChange);
                control.addQueryParams(this.queryParams);
                this.form.controls.push(control);
            });
        }
    }

    public removeSwitch(switchEl: HTMLElement) {
        // Remove the anchor switches
        this.anchorEls = this.anchorEls.filter(anchorEl => anchorEl != switchEl);

        // Remove the control-switches
        this.form.controls = this.form.controls.filter(control => {
            let removing = control.element == switchEl;
            if (removing) {
                control.removeQueryParams(this.queryParams);
            }
            return !removing;
        });
    }

    private hasInitialState() {
        return history.state != null;
    }

    private setInitialState(viewId: string) {
        history.replaceState(
            <ViewSwitcherVariables>{
                viewId: viewId,
                inputs: this.queryParams,
            }, '', '?' + formatQueryString(this.queryParams)
        );
    }

    public refreshView(viewId?: string) {
        if (!viewId) {
            window.location.reload();
            return Promise.resolve();
        }

        this.queryParams = selectPageParams();
        const variables = <ViewSwitcherVariables>{
            ...(history.state ?? {}),
            viewId,
            inputs: this.queryParams,
        };

        this.form.controls.forEach(control => control.saveState(variables));
        return this.sendViewRequest(variables, 'refresh');
    }

    private sendViewRequest(variables: ViewSwitcherVariables, mode: ViewRequestMode = 'push') {
        let viewEl = document.getElementById(variables.viewId)!;

        let request = http.get({
            namespace: 'ww-view',
            query: 'view',
            variables: JSON.stringify(variables)
        });

        let withToast = http.withToastOnError(request, 'Failed to update page');
        let withSpinner = http.withSpinner(withToast, viewEl, 500);
        return withSpinner.then(http.asText).then(html => {
            if (mode === 'pop') {
                this.previousViewIds.pop();           
            } else if (mode === 'push') {
                this.previousViewIds.push(variables.viewId);
            }
            viewEl.innerHTML = html;
        });
    }

    private onControlChange: FormControlChangeListener = (control) => {

        // Save initial state (if not already saved)
        let viewId = this.getViewIdForControl(control);
        let historyEnabled = control.element.dataset.historyEnabled != 'false';
        if (viewId && !this.hasInitialState() && historyEnabled) {
            this.setInitialState(viewId);
        }

        // Update query params and get resulting URL
        this.applyChangeToQueryParams(control, this.queryParams);
        let queryString = formatQueryString(this.queryParams);
        let newUrl = '?' + queryString;

        // No view specified? Then update page by navigating directly to the new URL
        if (!viewId) {
            window.location.search = newUrl;
            return;
        }

        // Normal case: update view by switching out the view asynchronously
        let variables = <ViewSwitcherVariables>{ viewId: viewId, inputs: this.queryParams };
        this.form.controls.forEach(c => c.saveState(variables));
        if (historyEnabled) { history.pushState(variables, '', newUrl); }
        this.sendViewRequest(variables, 'push');
    }

    private applyChangeToQueryParams(control: FormControl, params: QueryParams) {
        // First clear any parameters that should be removed
        // Useful when controls are used as mutually exclusive filters
        let clears = control.element.dataset.clears;
        if (clears) {
            for (let name of clears.split(' ')) {
                let toClear = this.form.getControl(name);
                if (toClear) {
                    toClear.setValue(null);
                    toClear.removeQueryParams(params);
                } else {
                    delete params[name];
                }
            }
        }
    
        // Then add new parameters from the control
        control.addQueryParams(params);
    }

    private getViewIdForControl(control: FormControl) {
        return control.element.dataset.viewId;
    }

    private onAnchorClick = (e: MouseEvent) => {
        // Advanced click: Allow for default browser behavior
        if (e.button || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) {
            return;
        }

        // Simple left click: stay on current page
        e.preventDefault();

        // Save initial state (if not already saved)
        let anchorEl = <HTMLAnchorElement> e.currentTarget;
        let viewId = anchorEl.dataset.viewId!;
        if (!this.hasInitialState()) {
            this.setInitialState(viewId);
        }

        // Save the new state
        let queryString = anchorEl.getAttribute('href')!;
        this.queryParams = selectQueryParams(queryString);
        let variables = <ViewSwitcherVariables>{
            viewId: viewId,
            inputs: this.queryParams,
        };

        history.pushState(variables, '', queryString);

        // Update the view
        this.sendViewRequest(variables, 'push');
    };

    private onStateChange = (e: PopStateEvent) => {
        // If another component is writing to the browser history, the destination state may not contain a viewId. Consider the case where the user runs a report, navigates to another section of the page, and then navigates back. In this case, we need to load the previous section of the page, but the popped state will have the report state, not the view switch state. 
        let state = e.state;
        if (!state?.viewId) {
            // Look at the URL to see if it contains the same view control params. If so, no update is necessary.
            // Note: This assumes that no other component is writing to the URL params. A safer bet would be to check each control individually with a new function like matchesParams(pageParams).
            const pageParams = selectPageParams();
            if (JSON.stringify(this.queryParams) === JSON.stringify(pageParams)) {
                return;
            }
            // Otherwise, we'll need to update the page based on the current URL parameters.
            // Since we currently do not store the original/default control vaues, a page refresh is the only reliable approach
            window.location.reload();
            return;
        }
        // The previous state has the correct url parameters, but the view id is not necessarily the correct one needed to restore the view. That view id refers to the view that was changed in order to get to the previous state, not the view that was changed most recently. That's why we need to save the view id of the most recent view change. (Or we could reload the page, but we'd like to avoid the overhead of a full page reload if possible.)

        // Example: switch out an inner view, then switch out an outer view, and finally hit the back button. In this case, the popped state would show the change done to the inner view, but the information about the outer view changing would be lost. If we simply swapped out the inner view (such as a data table), it would not match up with the outer view (such as a heading and toolbar for the data table, with the data table included). 
        if (this.previousViewIds.length > 0) {
            state.viewId = this.previousViewIds[this.previousViewIds.length - 1];
        }
        this.setVariables(state);
        this.sendViewRequest(state, 'pop');

        // The page params have likely changed as well so we need to update them
        // For example, the user may have navigated to a specific calendar, then hit the back button.
        // We would not want the calendar id to stick around in the query params.
        this.queryParams = selectPageParams();
    }

    private setVariables(variables: ViewSwitcherVariables) {
        // Controls without a view id may have a default value which is not captured in the query params
        // Example: a year filter with a default value of the current year. We wouldn't want to reset it to blank.
        // Besides, there is no need to update the state of such controls because they use hard refreshes.
        this.form.controls.filter(c => !!this.getViewIdForControl(c))
            .forEach(c => c.restoreState(variables));
    }
}

export class ViewSwitchController extends Controller<HTMLElement> {

    static viewSwitcher: ViewSwitcher;

    connect(): void {
        ViewSwitchController.viewSwitcher.addSwitch(this.element);
    }

    disconnect(): void {
        ViewSwitchController.viewSwitcher.removeSwitch(this.element);
    }
}

// TODO: use UrlSearchParams instead
export interface QueryParams {
    [key: string]: string;
}

function selectPageParams(): QueryParams {
    return selectQueryParams(window.location.search);
}

/**
 * Adapted from **improved solution** on https://stackoverflow.com/questions/8648892/how-to-convert-url-parameters-to-a-javascript-object
 */
function selectQueryParams(href: string): QueryParams {
    const qmarkLocation = href.indexOf('?');
    if (qmarkLocation === -1) {
        return {};
    }

    const queryString = href.substring(qmarkLocation + 1);
    const paramString = queryString.replace(/&/g, '","').replace(/=/g, '":"');
    return paramString == '' ? {} : JSON.parse(`{"${paramString}"}`, function (key, value) {
        return key === '' ? value : decodeURIComponent(value);
    });
}

/** Adapted from accepted answer on https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object */
function formatQueryString(params: QueryParams) {
    let encodedParams = [];
    for (let key in params) {
        let encodedKey = encodeURIComponent(key);
        let encodedValue = encodeURIComponent(params[key]);
        encodedParams.push(encodedKey + '=' + encodedValue);
    }
    return encodedParams.join('&');
}
